import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader, CButton } from '../../components/common';
import { FiCheck, FiPlus, FiChevronRight } from 'react-icons/fi';
import { IoWallet } from 'react-icons/io5';
import {
  fetchExpertDetails,
  createBookingOrder,
  createExpertBooking,
  fetchRazorpayKeyId,
  createWalletDepositOrder,
  verifyWalletDeposit,
  bookingCheckoutPreview,
  type BookingCheckoutPreview,
  type Expert,
} from '../../api';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface LocationState {
  expertId: string;
  selectedSlotDate: string;
  selectedSlotEndDate: string;
  bookingType: 'C' | 'V';
}

const TOPUP_PRESETS = [100, 500, 1000, 2000];

const BookingConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = location.state as LocationState;

  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(false);
  const [expertData, setExpertData] = useState<Expert | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Preview state
  const [preview, setPreview] = useState<BookingCheckoutPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [error, setError] = useState('');

  // Inline top-up state
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupProcessing, setTopupProcessing] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!id || !state) return;
    setPreviewLoading(true);
    setError('');
    try {
      const data = await bookingCheckoutPreview(parseInt(id), state.bookingType);
      setPreview(data);
    } catch {
      setError('Could not load checkout details. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  }, [id, state]);

  useEffect(() => {
    if (!state) {
      navigate(`/expert/${id}/book`);
      return;
    }
    loadExpertData();
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadExpertData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await fetchExpertDetails(parseInt(id));
      setExpertData(data);
    } catch (error) {
      console.error('Error fetching expert data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatRs = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return `\u20B9${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
    };
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) return;

    setTopupProcessing(true);
    setError('');
    try {
      const keyId = await fetchRazorpayKeyId();
      const order = await createWalletDepositOrder(amount);

      const options = {
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Yantrai',
        description: `Wallet Top-up - Rs ${amount}`,
        order_id: order.order_id,
        handler: async (response: any) => {
          try {
            await verifyWalletDeposit({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setTopupAmount('');
            setShowTopup(false);
            await loadPreview();
          } catch {
            setError('Top-up verification failed.');
          } finally {
            setTopupProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setTopupProcessing(false);
          },
        },
        theme: { color: colors.primary },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setError('Top-up payment failed.');
        setTopupProcessing(false);
      });
      rzp.open();
    } catch {
      setError('Could not initiate top-up.');
      setTopupProcessing(false);
    }
  };

  const handleProceed = async () => {
    if (!id || !state || !preview) return;
    setLoading(true);
    setError('');

    try {
      const useWallet = preview.payment_method !== 'razorpay_only';
      const result = await createBookingOrder(
        parseInt(id),
        state.bookingType,
        state.selectedSlotDate,
        state.selectedSlotEndDate,
        useWallet,
      );

      // --- Wallet-only: booking already created by backend ---
      if (result.payment_method === 'wallet_only') {
        setBookingSuccess(true);
        setLoading(false);
        return;
      }

      // --- Razorpay flow (hybrid or razorpay-only) ---
      const keyId = await fetchRazorpayKeyId();
      const options = {
        key: keyId,
        amount: result.amount,
        currency: result.currency,
        name: 'Yantrai',
        description: 'Expert Session Booking',
        order_id: result.order_id,
        handler: async (response: any) => {
          try {
            await createExpertBooking(parseInt(id), {
              start_time: state.selectedSlotDate,
              end_time: state.selectedSlotEndDate,
              booking_type: state.bookingType,
              razorpay_res: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            });
            setBookingSuccess(true);
          } catch {
            setError('Booking failed after payment. Contact support.');
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          },
        },
        theme: { color: colors.primary },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setError('Payment failed. Please try again.');
        setLoading(false);
      });
      rzp.open();
    } catch {
      setError('Could not initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const handleGoToBookings = () => {
    navigate('/bookings');
  };

  if (!state) {
    return null;
  }

  if (!expertData && !loading) {
    return null;
  }

  const { date, time } = formatDateTime(state.selectedSlotDate);
  const endTime = formatDateTime(state.selectedSlotEndDate).time;

  // Success Screen
  if (bookingSuccess) {
    return (
      <div
        className="p-4 min-h-screen pb-8 flex flex-col items-center justify-center"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: colors.green + '20' }}
        >
          {React.createElement(FiCheck as any, { size: 40, color: colors.green })}
        </div>

        <CText type="B24" color={colors.textColor} style={{ marginBottom: 8, textAlign: 'center' }}>
          Booking Requested!
        </CText>

        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 24, textAlign: 'center' }}>
          Your appointment with {expertData?.name} has been submitted. The expert will confirm your booking shortly.
        </CText>

        <div
          className="w-full rounded-xl p-4 mb-6"
          style={{ backgroundColor: colors.inputBackground }}
        >
          <div className="flex justify-between mb-2">
            <CText type="r14" color={colors.grayScale500}>Date</CText>
            <CText type="M14" color={colors.textColor}>{date}</CText>
          </div>
          <div className="flex justify-between mb-2">
            <CText type="r14" color={colors.grayScale500}>Time</CText>
            <CText type="M14" color={colors.textColor}>{time} - {endTime}</CText>
          </div>
          <div className="flex justify-between">
            <CText type="r14" color={colors.grayScale500}>Type</CText>
            <CText type="M14" color={colors.textColor}>
              {state.bookingType === 'C' ? 'Chat Session' : 'Video Call'}
            </CText>
          </div>
        </div>

        <CButton title="View My Bookings" onPress={handleGoToBookings} />
      </div>
    );
  }

  return (
    <div
      className="p-4 min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {loading && <CLoader />}

      <CHeader title="Order Summary" />

      {/* Expert Info Card */}
      <div
        className="rounded-xl p-4 mb-4 flex items-center gap-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold"
          style={{ backgroundColor: colors.primary, color: colors.white }}
        >
          {expertData?.name?.charAt(0) || 'E'}
        </div>
        <div className="flex-1">
          <CText type="B16" color={colors.textColor}>
            {expertData?.name || 'Expert'}
          </CText>
          <CText type="r12" color={colors.grayScale500}>
            {expertData?.sebi_qualification || ''}
          </CText>
          <CText type="r12" color={colors.grayScale500}>
            30 mins {state.bookingType === 'C' ? 'Chat' : 'Video Call'}
          </CText>
        </div>
        {preview && (
          <CText type="B18" color={colors.textColor}>
            {formatRs(preview.session_rate)}
          </CText>
        )}
      </div>

      {/* Booking Details */}
      <div
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        <CText type="M14" color={colors.textColor} style={{ marginBottom: 12 }}>
          Booking Details
        </CText>
        <div className="flex justify-between mb-2">
          <CText type="r14" color={colors.grayScale500}>Date</CText>
          <CText type="r14" color={colors.textColor}>{date}</CText>
        </div>
        <div className="flex justify-between">
          <CText type="r14" color={colors.grayScale500}>Time</CText>
          <CText type="r14" color={colors.textColor}>{time} - {endTime}</CText>
        </div>
      </div>

      {previewLoading ? (
        <div className="flex justify-center py-8">
          <CLoader />
        </div>
      ) : preview ? (
        <>
          {/* Price Breakdown */}
          <div
            className="rounded-xl p-4 mb-4 space-y-3"
            style={{
              backgroundColor: colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex justify-between">
              <CText type="r14" color={colors.grayScale500}>Expert Charges</CText>
              <CText type="r14" color={colors.textColor}>{formatRs(preview.session_rate)}</CText>
            </div>

            {preview.discount_pct > 0 && (
              <div className="flex justify-between">
                <CText type="r14" color={colors.green || '#1DCE5C'}>
                  -{preview.discount_pct}% Subscription Discount
                </CText>
                <CText type="r14" color={colors.green || '#1DCE5C'}>
                  -{formatRs(preview.discount_amount)}
                </CText>
              </div>
            )}

            <div className="flex justify-between">
              <CText type="r14" color={colors.grayScale500}>Platform Fee</CText>
              <CText type="r14" color={colors.textColor}>{formatRs(preview.platform_fee)}</CText>
            </div>

            {parseFloat(preview.tax) > 0 && (
              <div className="flex justify-between">
                <CText type="r14" color={colors.grayScale500}>Tax</CText>
                <CText type="r14" color={colors.textColor}>{formatRs(preview.tax)}</CText>
              </div>
            )}

            <div
              className="border-t pt-3 flex justify-between"
              style={{ borderColor: colors.borderColor }}
            >
              <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                Amount Due
              </CText>
              <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                {formatRs(preview.total_amount)}
              </CText>
            </div>

            {parseFloat(preview.wallet_deduction) > 0 && (
              <div className="flex justify-between">
                <div className="flex items-center gap-1">
                  {React.createElement(IoWallet as any, { size: 14, color: colors.primary })}
                  <CText type="r14" color={colors.primary}>Wallet</CText>
                </div>
                <CText type="r14" color={colors.primary}>
                  -{formatRs(preview.wallet_deduction)}
                </CText>
              </div>
            )}

            {parseFloat(preview.razorpay_amount) > 0 && (
              <div className="flex justify-between">
                <CText type="r14" color={colors.grayScale500}>Razorpay</CText>
                <CText type="r14" color={colors.textColor}>
                  {formatRs(preview.razorpay_amount)}
                </CText>
              </div>
            )}
          </div>

          {/* Wallet Balance + Top-up */}
          <div
            className="rounded-xl p-4 mb-4"
            style={{
              backgroundColor: colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {React.createElement(IoWallet as any, { size: 16, color: colors.primary })}
                <CText type="r14" color={colors.grayScale500}>Wallet Balance</CText>
              </div>
              <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                {formatRs(preview.wallet_balance)}
              </CText>
            </div>

            <button
              onClick={() => setShowTopup(!showTopup)}
              className="flex items-center gap-1 mt-2 hover:opacity-80"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {React.createElement(FiPlus as any, { size: 14, color: colors.primary })}
              <CText type="r12" color={colors.primary} style={{ fontWeight: 500 }}>
                Add Money to Wallet
              </CText>
              {React.createElement(FiChevronRight as any, {
                size: 14,
                color: colors.primary,
                style: { transform: showTopup ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' },
              })}
            </button>

            {showTopup && (
              <div className="mt-3 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {TOPUP_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setTopupAmount(String(preset))}
                      className="px-3 py-1.5 rounded-lg border text-sm transition-colors"
                      style={{
                        backgroundColor: topupAmount === String(preset)
                          ? colors.primary
                          : colors.backgroundColor,
                        borderColor: topupAmount === String(preset)
                          ? colors.primary
                          : colors.borderColor,
                        color: topupAmount === String(preset)
                          ? '#fff'
                          : colors.textColor,
                        cursor: 'pointer',
                      }}
                    >
                      {`\u20B9${preset}`}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="Amount"
                    min="1"
                    className="flex-1 px-3 py-2 rounded-lg border outline-none text-sm"
                    style={{
                      backgroundColor: colors.backgroundColor,
                      borderColor: colors.borderColor,
                      color: colors.textColor,
                    }}
                  />
                  <button
                    onClick={handleTopup}
                    disabled={topupProcessing || !topupAmount}
                    className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                    style={{
                      backgroundColor: colors.primary,
                      color: '#fff',
                      cursor: topupProcessing || !topupAmount ? 'not-allowed' : 'pointer',
                      border: 'none',
                    }}
                  >
                    {topupProcessing ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div
              className="p-3 rounded-lg mb-4"
              style={{ backgroundColor: (colors.alertColor || '#E82C81') + '15' }}
            >
              <CText type="r12" color={colors.alertColor || '#E82C81'}>
                {error}
              </CText>
            </div>
          )}

          {/* Smart Pay Button */}
          <button
            onClick={handleProceed}
            disabled={loading}
            className="w-full py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-50"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: colors.shadowLarge,
            }}
          >
            {loading ? (
              'Processing...'
            ) : preview.payment_method === 'wallet_only' ? (
              <span className="flex items-center justify-center gap-2">
                {React.createElement(FiCheck as any, { size: 18 })}
                Confirm & Pay from Wallet
              </span>
            ) : (
              `Pay ${formatRs(preview.razorpay_amount)} via Razorpay`
            )}
          </button>

          <CText
            type="r12"
            color={colors.grayScale500}
            style={{ marginTop: 12, textAlign: 'center' }}
          >
            By proceeding, you agree to our Terms & Conditions
          </CText>
        </>
      ) : (
        <div className="text-center py-8">
          <CText type="r14" color={colors.alertColor || '#E82C81'}>
            {error || 'Could not load checkout details.'}
          </CText>
          <button
            onClick={loadPreview}
            className="mt-4 px-4 py-2 rounded-lg text-sm"
            style={{
              backgroundColor: colors.primary,
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingConfirmation;
